import { UseSuspenseQueryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

import { Id, PagedParams, PagedResponse, getQueryKey, invalidateAfterDelete } from '@feature/utils';

import * as api from './TechnologicalTransferApi';
import {
  ReclaimRequest,
  TechnologicalTransfer,
  TechnologicalTransferRequest,
  TechnologicalTransferSearchRequest,
} from './TechnologicalTransferTypes';

const technologicalTransferKey = getQueryKey('technological-transfer');

export function getTechnologicalTransferPagedOptions(
  params: PagedParams,
): UseSuspenseQueryOptions<PagedResponse<TechnologicalTransfer>> {
  return {
    queryKey: technologicalTransferKey.paged(params),
    queryFn: () => api.getPaged(params),
  };
}

export function getTechnologicalTransferOptions(
  technologicalTransferId: Id,
): UseSuspenseQueryOptions<TechnologicalTransfer> {
  return {
    queryKey: technologicalTransferKey.one(technologicalTransferId),
    queryFn: () => api.get(technologicalTransferId),
  };
}

export function useTechnologicalTransferEdit(technologicalTransferId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: TechnologicalTransferRequest) => api.edit(technologicalTransferId, request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: technologicalTransferKey.root(),
      }),
  });
}

export function useTechnologicalTransferCreate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: TechnologicalTransferRequest) => api.create(request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: technologicalTransferKey.root(),
      }),
  });
}

export function useTechnologicalTransferDelete(technologicalTransferId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.deleteById(technologicalTransferId),
    onSuccess: () => invalidateAfterDelete(queryClient, technologicalTransferKey, technologicalTransferId),
  });
}

export function useSearchTechnologicalTransferByIdentifiers() {
  return useMutation({
    mutationFn: (request: TechnologicalTransferSearchRequest) => api.searchByIdentifiers(request),
  });
}

export function useReclaimAuthorship() {
  return useMutation({
    mutationFn: ({ technologicalTransferId, authorId }: ReclaimRequest) =>
      api.reclaimAuthorship(technologicalTransferId, authorId),
  });
}
